"use client";

import { useEffect, useId, useState } from "react";
import type { ReactNode, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { track } from "@/lib/analytics";

/* ── Device store helpers ──────────────────────────────────────────────────
   All persistence on this site is localStorage only — data stays in the
   user's browser, nothing is sent anywhere. Reads happen after mount so
   server and client markup stay identical (static export hydration). */

function readDeviceStore<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeDeviceStore(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full / blocked — features degrade to non-persistent */
  }
}

function removeDeviceStore(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/* Shared client-side primitives for /tools pages. Keep every tool visually
   consistent: white cards, ink labels, emerald focus, risk-band verdicts. */

const inputBase =
  "w-full rounded-lg border border-line-2 bg-white px-3.5 py-2.5 text-[0.95rem] text-ink placeholder:text-muted/70 focus:border-emerald focus:outline-none focus:ring-2 focus:ring-emerald/25";

export function Field({ label, hint, children, className = "" }: { label: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-[0.85rem] font-semibold text-ink">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[0.78rem] text-muted">{hint}</span>}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input type="text" {...props} className={`${inputBase} ${props.className ?? ""}`} />;
}

/* ── Remembered names (people / plant libraries) ───────────────────────────
   A TextInput that quietly remembers what was typed (on this device only)
   and offers it back as autocomplete next time. One store per kind of thing:
   "people" for names, "plant" for tools/equipment, "sites" for site names —
   so a name saved on one tool appears on every tool that asks for the same
   kind of thing. */

const NAME_STORE_PREFIX = "ramsdocs.names.";
const NAME_STORE_CAP = 25;

export type RememberStore = "people" | "plant" | "sites";

function readNames(store: RememberStore): string[] {
  return readDeviceStore<string[]>(NAME_STORE_PREFIX + store) ?? [];
}

/** Wipes one remembered-names list (exposed for "forget" controls). */
export function clearRememberedNames(store: RememberStore) {
  removeDeviceStore(NAME_STORE_PREFIX + store);
}

export function RememberedTextInput({
  store,
  onBlur,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { store: RememberStore }) {
  const listId = useId();
  const [options, setOptions] = useState<string[]>([]);

  useEffect(() => {
    setOptions(readNames(store));
  }, [store]);

  const remember = (raw: string) => {
    const v = raw.trim();
    if (v.length < 3) return; // too short to be a useful suggestion
    const existing = readNames(store).filter((o) => o.toLowerCase() !== v.toLowerCase());
    const next = [v, ...existing].slice(0, NAME_STORE_CAP);
    writeDeviceStore(NAME_STORE_PREFIX + store, next);
    setOptions(next);
  };

  return (
    <>
      <input
        type="text"
        {...props}
        list={listId}
        onBlur={(e) => {
          remember(e.currentTarget.value);
          onBlur?.(e);
        }}
        className={`${inputBase} ${props.className ?? ""}`}
      />
      <datalist id={listId}>
        {options.map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>
    </>
  );
}

/** One-line disclosure + forget control for the remembered-names stores.
    Place once per tool that uses RememberedTextInput. */
export function RememberedNamesNote({ stores }: { stores: RememberStore[] }) {
  const [cleared, setCleared] = useState(false);
  const labels: Record<RememberStore, string> = {
    people: "names",
    plant: "plant & tools",
    sites: "site names",
  };
  return (
    <p className="text-[0.78rem] text-muted">
      {stores.map((s) => labels[s]).join(", ").replace(/, ([^,]*)$/, " and $1").replace(/^./, (c) => c.toUpperCase())}{" "}
      you type are remembered on this device for next time — nothing is sent to us.{" "}
      <button
        type="button"
        onClick={() => {
          stores.forEach(clearRememberedNames);
          setCleared(true);
          setTimeout(() => setCleared(false), 2000);
        }}
        className="font-semibold text-emerald-deep underline hover:text-emerald"
      >
        {cleared ? "Forgotten ✓" : "Forget them"}
      </button>
    </p>
  );
}

export function NumberInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input type="number" inputMode="decimal" {...props} className={`${inputBase} ${props.className ?? ""}`} />;
}

export function SelectInput(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${inputBase} ${props.className ?? ""}`} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={3} {...props} className={`${inputBase} ${props.className ?? ""}`} />;
}

export function CheckboxRow({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-line bg-white px-3.5 py-3 transition-colors hover:border-line-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-emerald"
      />
      <span className="text-[0.92rem] leading-snug text-body">{children}</span>
    </label>
  );
}

export type Verdict = "low" | "medium" | "high" | "veryhigh";

const verdictStyles: Record<Verdict, string> = {
  low: "border-risk-low/40 bg-emerald-tint text-emerald-deep",
  medium: "border-risk-medium/40 bg-[#fdf6e3] text-[#8a6400]",
  high: "border-risk-high/40 bg-[#fef0e6] text-[#b34e10]",
  veryhigh: "border-risk-veryhigh/40 bg-[#fdecec] text-[#a31616]",
};

/** Big coloured verdict banner: the tool's answer at a glance. */
export function ResultBanner({ level, title, children }: { level: Verdict; title: string; children?: ReactNode }) {
  return (
    <div className={`rounded-xl border px-5 py-4 ${verdictStyles[level]}`}>
      <div className="font-display text-lg font-bold">{title}</div>
      {children && <div className="mt-1 text-[0.92rem] leading-relaxed opacity-90">{children}</div>}
    </div>
  );
}

/** Prints the current page; print CSS shows only .print-area content. */
export function PrintButton({ tool, label = "Print / save as PDF" }: { tool: string; label?: string }) {
  return (
    <button
      type="button"
      onClick={() => {
        track("tool_print", { tool });
        window.print();
      }}
      className="inline-flex items-center justify-center gap-2 rounded-full border border-line-2 px-6 py-3 text-[0.95rem] font-semibold text-ink transition-all hover:border-ink hover:bg-mist"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 9V2h12v7" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 14h12v8H6z" />
      </svg>
      {label}
    </button>
  );
}

/**
 * Print button that warns when required fields are missing. If `missing` is
 * non-empty the first click expands a warning listing what's incomplete; the
 * user can go back and fill the gaps or explicitly print anyway. Used on
 * permits and other high-risk documents that should not leave the site with
 * silent blanks.
 */
export function GuardedPrintButton({
  tool,
  label = "Print / save as PDF",
  missing,
  warningTitle = "This document has missing required fields",
}: {
  tool: string;
  label?: string;
  /** Human-readable names of required fields/checks not yet complete. */
  missing: string[];
  warningTitle?: string;
}) {
  const [warned, setWarned] = useState(false);

  function doPrint() {
    track("tool_print", { tool, missing: missing.length });
    window.print();
  }

  if (missing.length === 0 || warned) {
    return (
      <div className="space-y-3">
        {warned && missing.length > 0 && (
          <div role="alert" className="rounded-xl border border-risk-high/40 bg-[#fef0e6] px-4 py-3 text-[#b34e10]">
            <div className="text-[0.9rem] font-bold">{warningTitle}</div>
            <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-[0.85rem]">
              {missing.slice(0, 8).map((m) => (
                <li key={m}>{m}</li>
              ))}
              {missing.length > 8 && <li>…and {missing.length - 8} more</li>}
            </ul>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setWarned(false)}
                className="rounded-full bg-ink px-4 py-2 text-[0.85rem] font-semibold text-white hover:bg-ink/85"
              >
                Go back and complete
              </button>
              <button
                type="button"
                onClick={doPrint}
                className="rounded-full border border-[#b34e10]/40 px-4 py-2 text-[0.85rem] font-semibold text-[#b34e10] hover:bg-[#b34e10]/10"
              >
                Print anyway (with blanks)
              </button>
            </div>
          </div>
        )}
        {(missing.length === 0 || !warned) && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={doPrint}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-line-2 px-6 py-3 text-[0.95rem] font-semibold text-ink transition-all hover:border-ink hover:bg-mist"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9V2h12v7" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 14h12v8H6z" />
              </svg>
              {label}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex justify-end">
      <button
        type="button"
        onClick={() => setWarned(true)}
        className="inline-flex items-center justify-center gap-2 rounded-full border border-line-2 px-6 py-3 text-[0.95rem] font-semibold text-ink transition-all hover:border-ink hover:bg-mist"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9V2h12v7" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 14h12v8H6z" />
        </svg>
        {label}
      </button>
    </div>
  );
}

/**
 * Copy-to-clipboard button with a visible "Copied" confirmation and an
 * aria-live status for screen readers.
 */
export function CopyButton({
  tool,
  buildText,
  label = "Copy as text",
}: {
  tool: string;
  /** Builds the text to copy at click time. */
  buildText: () => string;
  label?: string;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  async function copy() {
    try {
      await navigator.clipboard.writeText(buildText());
      track("tool_copy", { tool });
      setState("copied");
    } catch {
      setState("failed");
    }
    setTimeout(() => setState("idle"), 2500);
  }

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={copy}
        className={`inline-flex items-center justify-center gap-2 rounded-full border px-6 py-3 text-[0.95rem] font-semibold transition-all ${
          state === "copied"
            ? "border-emerald bg-emerald-tint text-emerald-deep"
            : "border-line-2 text-ink hover:border-ink hover:bg-mist"
        }`}
      >
        {state === "copied" ? (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m4 12 5 5L20 6" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        )}
        {state === "copied" ? "Copied to clipboard" : state === "failed" ? "Copy failed — select and copy manually" : label}
      </button>
      {/* Screen-reader announcement of the copy outcome. */}
      <span aria-live="polite" className="sr-only">
        {state === "copied" ? "Copied to clipboard" : state === "failed" ? "Copy failed" : ""}
      </span>
    </span>
  );
}

/**
 * Accessible segmented control (Yes / No / Partly etc). Renders a fieldset
 * with a legend and native radio inputs styled as pills, so screen readers
 * announce the question with each option and arrow keys work as expected.
 * The legend is visually hidden by default because the question text is
 * usually already rendered next to the control.
 */
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Classes applied when the option is selected (colour-codes the answer). */
  activeCls: string;
}

export function SegmentedControl<T extends string>({
  legend,
  name,
  value,
  onChange,
  options,
  className = "",
}: {
  /** The question this control answers — read to screen readers. */
  legend: string;
  /** Unique radio-group name per question. */
  name: string;
  value: T | undefined;
  onChange: (v: T) => void;
  options: SegmentedOption<T>[];
  className?: string;
}) {
  return (
    <fieldset className={`m-0 border-0 p-0 ${className}`}>
      <legend className="sr-only">{legend}</legend>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <label
              key={opt.value}
              className={`cursor-pointer rounded-full border px-3 py-1.5 text-[0.78rem] font-semibold transition-colors focus-within:ring-2 focus-within:ring-emerald/40 ${
                active ? opt.activeCls : "border-line-2 bg-white text-muted hover:border-ink hover:text-ink"
              }`}
            >
              <input
                type="radio"
                name={name}
                value={opt.value}
                checked={active}
                onChange={() => onChange(opt.value)}
                className="sr-only"
              />
              {opt.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * Sticky completion summary for long forms: progress bar, count and the next
 * missing item. Place at the top of the result column.
 */
export function CompletionPanel({
  items,
  title = "Completion",
}: {
  /** Required fields/sections in document order. */
  items: { label: string; done: boolean }[];
  title?: string;
}) {
  const done = items.filter((i) => i.done).length;
  const next = items.find((i) => !i.done);
  const pct = items.length > 0 ? Math.round((done / items.length) * 100) : 0;
  return (
    <div className="print-hide card shadow-soft p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[0.78rem] font-bold uppercase tracking-wide text-muted">{title}</span>
        <span className={`font-display text-sm font-bold ${done === items.length ? "text-emerald-deep" : "text-ink"}`}>
          {done} of {items.length}
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-mist" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${title}: ${done} of ${items.length} complete`}>
        <div
          className={`h-full rounded-full transition-all ${done === items.length ? "bg-risk-low" : "bg-emerald"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {next ? (
        <p className="mt-2 text-[0.82rem] text-body">
          <span className="font-semibold text-ink">Next:</span> {next.label}
        </p>
      ) : (
        <p className="mt-2 text-[0.82rem] font-semibold text-emerald-deep">All required sections complete.</p>
      )}
    </div>
  );
}

/**
 * Consistent "rules/data last reviewed" line for tools whose underlying rules
 * change (tax rates, card schemes, reporting regs, hospital lists). Shown in
 * the result panel and on the printed output.
 */
export function DataReviewedNote({ children, date }: { children?: ReactNode; date: string }) {
  return (
    <p className="mt-3 rounded-lg bg-mist px-3 py-2 text-[0.78rem] leading-relaxed text-muted">
      <span className="font-semibold text-ink">Rules &amp; data last reviewed {date}.</span>{" "}
      {children ?? "These rules change — check the official source before relying on the result."}
    </p>
  );
}

/**
 * Horizontal-scroll wrapper for wide tables so they behave on mobile: scrolls
 * inside the card instead of stretching the page, with a small swipe hint.
 */
export function TableScroll({ children, hint = true }: { children: ReactNode; hint?: boolean }) {
  return (
    <div>
      {hint && <p className="mb-1 text-right text-[0.68rem] uppercase tracking-wide text-muted/80 sm:hidden print-hide">swipe to see all columns →</p>}
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

/* ── Shared evidence model ─────────────────────────────────────────────────
   Principal contractors and inspectors look for evidence that a document is
   current, owned by a named competent person, and was actually briefed. Every
   printable safety document carries the same control block: prepared by /
   role / company / date / review date / revision, plus prepared / reviewed /
   briefed signature lines on the printed output. */

export interface Evidence {
  preparedBy: string;
  role: string;
  company: string;
  /** ISO yyyy-mm-dd. Defaults to today. */
  date: string;
  /** ISO yyyy-mm-dd. Optional but strongly encouraged. */
  reviewDate: string;
  revision: string;
}

export function defaultEvidence(): Evidence {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return {
    preparedBy: "",
    role: "",
    company: "",
    date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
    reviewDate: "",
    revision: "1",
  };
}

/** The evidence fields a document shouldn't ship without — spread into a
    tool's requiredItems list. */
export function evidenceRequiredItems(e: Evidence): { label: string; done: boolean }[] {
  return [
    { label: "Prepared by (name)", done: e.preparedBy.trim().length > 0 },
    { label: "Company", done: e.company.trim().length > 0 },
  ];
}

/* localStorage key for the remembered identity fields. Only name/role/company
   persist — date and revision are per-document. Data never leaves the device. */
const EVIDENCE_STORE_KEY = "ramsdocs.evidence";

interface StoredEvidence {
  preparedBy: string;
  role: string;
  company: string;
}

function readStoredEvidence(): StoredEvidence | null {
  try {
    const raw = window.localStorage.getItem(EVIDENCE_STORE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (typeof v !== "object" || v === null) return null;
    return {
      preparedBy: typeof v.preparedBy === "string" ? v.preparedBy : "",
      role: typeof v.role === "string" ? v.role : "",
      company: typeof v.company === "string" ? v.company : "",
    };
  } catch {
    return null;
  }
}

/**
 * Evidence state that remembers who you are between visits (name, role,
 * company in this browser's localStorage — nothing is sent anywhere). Loads
 * after mount to keep server/client markup identical; saves on every change.
 */
export function useSavedEvidence(seed?: Partial<Evidence>): [Evidence, (e: Evidence) => void, () => void] {
  const [evidence, setEvidence] = useState<Evidence>(() => ({ ...defaultEvidence(), ...seed }));

  // Hydration-safe: merge the remembered identity in after mount only.
  useEffect(() => {
    const stored = readStoredEvidence();
    if (stored && (stored.preparedBy || stored.role || stored.company)) {
      setEvidence((e) => ({ ...e, ...stored }));
    }
  }, []);

  const update = (e: Evidence) => {
    setEvidence(e);
    try {
      window.localStorage.setItem(
        EVIDENCE_STORE_KEY,
        JSON.stringify({ preparedBy: e.preparedBy, role: e.role, company: e.company }),
      );
    } catch {
      /* storage full / blocked — the form still works, it just won't persist */
    }
  };

  const clear = () => {
    try {
      window.localStorage.removeItem(EVIDENCE_STORE_KEY);
    } catch {
      /* ignore */
    }
    setEvidence((e) => ({ ...e, preparedBy: "", role: "", company: "" }));
  };

  return [evidence, update, clear];
}

/** Form card for the document-control fields. Pass `onClear` (from
    useSavedEvidence) to show the remembered-on-this-device hint + reset. */
export function EvidenceFields({
  value,
  onChange,
  onClear,
  heading = "Document control",
  intro = "Who owns this document and when it gets looked at again — the evidence reviewers check first.",
}: {
  value: Evidence;
  onChange: (e: Evidence) => void;
  onClear?: () => void;
  heading?: string;
  intro?: string;
}) {
  const set = (patch: Partial<Evidence>) => onChange({ ...value, ...patch });
  return (
    <div className="card shadow-soft p-6">
      <h2 className="text-xl">{heading}</h2>
      <p className="mt-1 text-sm text-muted">{intro}</p>
      {onClear && (
        <p className="mt-1.5 text-[0.78rem] text-muted">
          Name, role and company are remembered on this device only — nothing is sent to us.{" "}
          <button type="button" onClick={onClear} className="font-semibold text-emerald-deep underline hover:text-emerald">
            Forget them
          </button>
        </p>
      )}
      {value.reviewDate && (
        <p className="mt-3">
          <button
            type="button"
            onClick={() => downloadReviewIcs(value)}
            className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-3.5 py-1.5 text-[0.82rem] font-semibold text-ink hover:border-ink hover:bg-mist"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            Add the review date to your calendar (.ics)
          </button>
        </p>
      )}
      <BrandingControl />
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Field label="Prepared by">
          <TextInput value={value.preparedBy} onChange={(e) => set({ preparedBy: e.target.value })} placeholder="Name" />
        </Field>
        <Field label="Role">
          <TextInput value={value.role} onChange={(e) => set({ role: e.target.value })} placeholder="e.g. Site supervisor" />
        </Field>
        <Field label="Company">
          <TextInput value={value.company} onChange={(e) => set({ company: e.target.value })} placeholder="Company name" />
        </Field>
        <Field label="Date">
          <TextInput type="date" value={value.date} onChange={(e) => set({ date: e.target.value })} />
        </Field>
        <Field label="Review date" hint="When this document must be looked at again.">
          <TextInput type="date" value={value.reviewDate} onChange={(e) => set({ reviewDate: e.target.value })} />
        </Field>
        <Field label="Revision">
          <TextInput value={value.revision} onChange={(e) => set({ revision: e.target.value })} placeholder="e.g. 1" />
        </Field>
      </div>
    </div>
  );
}

/* ── Company branding ──────────────────────────────────────────────────────
   A logo saved once (on this device) appears on every printed document's
   control block — the letterhead touch reviewers expect from paid tools. */

const BRANDING_KEY = "ramsdocs.branding";

function useBrandingLogo(): [string | null, (dataUrl: string | null) => void] {
  const [logo, setLogo] = useState<string | null>(null);

  useEffect(() => {
    const stored = readDeviceStore<{ logo?: string }>(BRANDING_KEY);
    if (stored?.logo) setLogo(stored.logo);
  }, []);

  const update = (dataUrl: string | null) => {
    setLogo(dataUrl);
    if (dataUrl) writeDeviceStore(BRANDING_KEY, { logo: dataUrl });
    else removeDeviceStore(BRANDING_KEY);
  };

  return [logo, update];
}

/** Reads an image file, downscales it to a sane logo size and returns a
    PNG data URL (preserves transparency). */
function fileToLogoDataUrl(file: File, maxEdge = 400): Promise<string | null> {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/")) return resolve(null);
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = () => {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

/** Downloads an all-day .ics calendar event for the document's review date —
    the static-site answer to "expiry reminders": the reminder lives in the
    user's own calendar, nothing is stored or sent. */
function downloadReviewIcs(e: Evidence) {
  const day = e.reviewDate.replace(/-/g, "");
  if (!/^\d{8}$/.test(day)) return;
  const summary = `Review safety document${e.company.trim() ? ` — ${e.company.trim()}` : ""} (prepared ${e.date || "earlier"})`;
  const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//RamsDocs//Free tools//EN",
    "BEGIN:VEVENT",
    `UID:ramsdocs-review-${day}-${stamp}`,
    `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${day}`,
    `SUMMARY:${summary.replace(/[\n,;]/g, " ")}`,
    "DESCRIPTION:Created with the free RamsDocs tools — review the document and bump its revision if anything has changed.",
    "BEGIN:VALARM",
    "TRIGGER:-P7D",
    "ACTION:DISPLAY",
    "DESCRIPTION:Document review due in 7 days",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `document-review-${e.reviewDate}.ics`;
  a.click();
  URL.revokeObjectURL(url);
  track("tool_review_ics", {});
}

function fmtIsoLong(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

/** Logo upload row inside EvidenceFields — saved once per device, shown on
    every printed document-control block. */
function BrandingControl() {
  const [logo, setLogo] = useBrandingLogo();
  const inputId = useId();
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4">
      {logo ? (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={logo} alt="Your company logo" className="h-10 max-w-[140px] object-contain" />
          <button
            type="button"
            onClick={() => setLogo(null)}
            className="text-[0.82rem] font-semibold text-muted underline hover:text-risk-veryhigh"
          >
            Remove logo
          </button>
        </>
      ) : (
        <>
          <label
            htmlFor={inputId}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-line-2 px-3.5 py-1.5 text-[0.82rem] font-semibold text-ink hover:border-ink hover:bg-mist"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
            </svg>
            Add your company logo
          </label>
          <input
            id={inputId}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) {
                const url = await fileToLogoDataUrl(f);
                if (url) setLogo(url);
              }
              e.target.value = "";
            }}
          />
          <span className="text-[0.78rem] text-muted">
            Appears on every printed document — saved on this device only, never uploaded.
          </span>
        </>
      )}
    </div>
  );
}

/** A dashed signature line with a caption — printed output only. */
export function SignatureLine({ caption }: { caption: string }) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-end gap-4">
      <div>
        <div className="mt-5 border-b border-dashed border-ink/50" />
        <div className="mt-1 text-[0.72rem] uppercase tracking-wide text-muted">{caption}</div>
      </div>
      <div>
        <div className="mt-5 w-28 border-b border-dashed border-ink/50" />
        <div className="mt-1 text-[0.72rem] uppercase tracking-wide text-muted">Date</div>
      </div>
    </div>
  );
}

/** Printed document-control + sign-off block. Place at the end of the
    print-area, before the PrintFooter. */
export function EvidencePrint({
  value,
  briefing = true,
}: {
  value: Evidence;
  /** Include the "briefed to the workforce" line (on for site documents,
      off for desk-side calculators). */
  briefing?: boolean;
}) {
  const line = (v: string) => v.trim() || "________________";
  const [logo] = useBrandingLogo();
  return (
    <div className="mt-5 avoid-break rounded-lg border border-line p-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-[0.95rem] font-bold uppercase tracking-wide text-ink">
          Document control & sign-off
        </h3>
        {logo && (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img src={logo} alt={`${value.company.trim() || "Company"} logo`} className="h-9 max-w-[120px] shrink-0 object-contain" />
        )}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[0.84rem] sm:grid-cols-3">
        <div><span className="text-muted">Prepared by:</span> <span className="font-semibold text-ink">{line(value.preparedBy)}</span></div>
        <div><span className="text-muted">Role:</span> <span className="font-semibold text-ink">{line(value.role)}</span></div>
        <div><span className="text-muted">Company:</span> <span className="font-semibold text-ink">{line(value.company)}</span></div>
        <div><span className="text-muted">Date:</span> <span className="font-semibold text-ink">{fmtIsoLong(value.date) || "________________"}</span></div>
        <div><span className="text-muted">Review date:</span> <span className="font-semibold text-ink">{fmtIsoLong(value.reviewDate) || "________________"}</span></div>
        <div><span className="text-muted">Revision:</span> <span className="font-semibold text-ink">{line(value.revision)}</span></div>
      </div>
      <SignatureLine caption={`Prepared by — ${value.preparedBy.trim() || "name"}`} />
      <SignatureLine caption="Reviewed / approved by — competent person or supervisor" />
      {briefing && <SignatureLine caption="Briefed to the workforce by — name (attach briefing record where used)" />}
    </div>
  );
}

/* ── Action conversion ─────────────────────────────────────────────────────
   Checkers are more useful when failed checks become an action plan: each gap
   gets an owner and a due date and prints as a trackable list. */

export interface ActionEntry {
  owner: string;
  due: string; // ISO yyyy-mm-dd
  done?: boolean;
}

export type ActionValues = Record<string, ActionEntry>;

const ACTIONS_STORE_PREFIX = "ramsdocs.actions.";

/**
 * Action-plan state persisted on this device, so a checker can be re-opened
 * later to tick actions off and see what's overdue. Returns
 * [values, setAction, clearAll].
 */
export function useSavedActions(
  toolKey: string,
): [ActionValues, (id: string, patch: Partial<ActionEntry>) => void, () => void] {
  const [values, setValues] = useState<ActionValues>({});

  useEffect(() => {
    const stored = readDeviceStore<ActionValues>(ACTIONS_STORE_PREFIX + toolKey);
    if (stored) setValues(stored);
  }, [toolKey]);

  const setAction = (id: string, patch: Partial<ActionEntry>) =>
    setValues((a) => {
      const next = { ...a, [id]: { ...(a[id] ?? { owner: "", due: "" }), ...patch } };
      writeDeviceStore(ACTIONS_STORE_PREFIX + toolKey, next);
      return next;
    });

  const clearAll = () => {
    removeDeviceStore(ACTIONS_STORE_PREFIX + toolKey);
    setValues({});
  };

  return [values, setAction, clearAll];
}

function isOverdue(v: ActionEntry | undefined): boolean {
  if (!v || v.done || !v.due) return false;
  const d = new Date(v.due + "T23:59:59");
  return !isNaN(d.getTime()) && d.getTime() < Date.now();
}

/** Screen-side inputs: one owner/due/done row per open item. */
export function ActionPlanner({
  items,
  values,
  onChange,
  onClearAll,
  heading = "Turn the gaps into actions",
  intro = "Give each open item an owner and a due date — the printed report becomes an action plan, not just a score.",
}: {
  items: { id: string; label: string }[];
  values: ActionValues;
  onChange: (id: string, patch: Partial<ActionEntry>) => void;
  /** From useSavedActions — shows the saved-on-device note + reset. */
  onClearAll?: () => void;
  heading?: string;
  intro?: string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="card shadow-soft p-6 print-hide">
      <h2 className="text-xl">{heading}</h2>
      <p className="mt-1 text-sm text-muted">{intro}</p>
      <div className="mt-4 space-y-3">
        {items.map((it) => {
          const v = values[it.id] ?? { owner: "", due: "" };
          const overdue = isOverdue(values[it.id]);
          return (
            <div
              key={it.id}
              className={`rounded-lg border p-3.5 ${
                v.done ? "border-emerald/40 bg-emerald-tint/40" : overdue ? "border-risk-veryhigh/40 bg-[#fdecec]" : "border-line bg-mist/50"
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className={`text-[0.9rem] font-semibold leading-snug ${v.done ? "text-muted line-through" : "text-ink"}`}>
                  {it.label}
                </div>
                {overdue && (
                  <span className="rounded-full bg-risk-veryhigh/10 px-2 py-0.5 text-[0.64rem] font-bold uppercase tracking-wide text-risk-veryhigh">
                    Overdue
                  </span>
                )}
              </div>
              <div className="mt-2.5 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                <Field label="Owner">
                  <RememberedTextInput
                    store="people"
                    value={v.owner}
                    onChange={(e) => onChange(it.id, { owner: e.target.value })}
                    placeholder="Who closes this out"
                  />
                </Field>
                <Field label="Due date">
                  <TextInput type="date" value={v.due} onChange={(e) => onChange(it.id, { due: e.target.value })} />
                </Field>
                <label className="flex cursor-pointer items-center gap-2 pb-2.5 text-[0.85rem] font-semibold text-ink">
                  <input
                    type="checkbox"
                    checked={v.done === true}
                    onChange={(e) => onChange(it.id, { done: e.target.checked })}
                    className="h-4 w-4 accent-emerald"
                  />
                  Done
                </label>
              </div>
            </div>
          );
        })}
      </div>
      {onClearAll && (
        <p className="mt-4 text-[0.78rem] text-muted">
          Owners, dates and progress are saved on this device, so you can come back and tick actions off —
          nothing is sent to us.{" "}
          <button type="button" onClick={onClearAll} className="font-semibold text-emerald-deep underline hover:text-emerald">
            Clear saved actions
          </button>
        </p>
      )}
    </div>
  );
}

/** Printed action plan table with a close-out tick column. */
export function ActionPlanPrint({
  items,
  values,
  heading = "Action plan",
}: {
  items: { id: string; label: string }[];
  values: ActionValues;
  heading?: string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mt-5 avoid-break">
      <h3 className="font-display text-[0.95rem] font-bold uppercase tracking-wide text-ink">{heading}</h3>
      <TableScroll>
        <table className="mt-2 w-full min-w-[480px] text-[0.85rem]">
          <thead>
            <tr className="border-b border-line text-left text-[0.72rem] uppercase tracking-wide text-muted">
              <th className="w-6 py-1.5 pr-2 font-semibold">#</th>
              <th className="py-1.5 pr-2 font-semibold">Action</th>
              <th className="py-1.5 pr-2 font-semibold">Owner</th>
              <th className="py-1.5 pr-2 font-semibold">Due</th>
              <th className="w-14 py-1.5 font-semibold">Done</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const v = values[it.id] ?? { owner: "", due: "" };
              return (
                <tr key={it.id} className="border-b border-line/60 align-top">
                  <td className="py-2 pr-2 text-muted">{i + 1}</td>
                  <td className="py-2 pr-2 text-ink">{it.label}</td>
                  <td className="py-2 pr-2 text-body">{v.owner.trim() || "________"}</td>
                  <td className="py-2 pr-2 text-body">{fmtIsoLong(v.due) || "________"}</td>
                  <td className="py-2">
                    {v.done ? (
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-emerald bg-emerald text-[0.7rem] text-white" aria-label="done">
                        ✓
                      </span>
                    ) : (
                      <span className="inline-block h-4 w-4 rounded border border-ink/50" aria-hidden />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroll>
    </div>
  );
}

/** Fire once when a tool produces a meaningful result (for funnel measurement). */
export function trackToolResult(tool: string, props: Record<string, unknown> = {}) {
  track("tool_result", { tool, ...props });
}

/** Standard footer block on every printable output. */
export function PrintFooter({ toolName }: { toolName: string }) {
  return (
    <div className="mt-6 border-t border-line pt-3 text-[0.72rem] leading-relaxed text-muted">
      Generated with the free {toolName} at ramsdocs.co.uk. This record supports — and does not replace —
      competent-person review, site-specific judgement and your legal duties.
    </div>
  );
}
